import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { FaFileUpload, FaUserCheck, FaRegClock } from "react-icons/fa";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import "./RequesForReviewer.css";
const RequestForReviewer = () => {
  const [displayName, setDisplayName] = useState("");
  const [loggedInEmail, setLoggedInEmail] = useState("");
  const [role, setRole] = useState("");
  const [reviewerRequestStatus, setReviewerRequestStatus] = useState(null);
  const [expertiseFields, setExpertiseFields] = useState({
    domain1: "",
    domain2: "",
    domain3: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [domains, setDomains] = useState([]); // State to store fetched domains
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to check the reviewer request status
  const checkApprovalStatus = async () => {
    const email = localStorage.getItem("email");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/reviewer/check/${email}`
      );

      setReviewerRequestStatus(response.data.reviwerrequestStatus);
    } catch (error) {
      console.error("Error checking approval status:", error);
    }
  };

  // Fetch user data and request status
  useEffect(() => {
    const email = localStorage.getItem("email");
    const displayName = localStorage.getItem("displayName");
    const role = localStorage.getItem("role");

    setLoggedInEmail(email);
    setDisplayName(displayName);
    setRole(role);

    checkApprovalStatus(); // Fetch status based on email and role
  }, []);

  // Fetch domains from the server
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/reviewer/get-domain`
        );
        setDomains(response.data.map((domain) => domain.mainDomain)); // Set fetched main domains in the state
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error fetching domains",
          text: "Unable to retrieve domains from the server",
        });
      }
    };

    fetchDomains();
  }, []);

  const handleExpertiseChange = (e) => {
    setExpertiseFields({
      ...expertiseFields,
      [e.target.name]: e.target.value,
    });
  };

  const handleCvUpload = (e) => {
    setCvFile(e.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Ensure at least one expertise field is selected
    if (
      !expertiseFields.domain1 &&
      !expertiseFields.domain2 &&
      !expertiseFields.domain3
    ) {
      Swal.fire({
        icon: "error",
        title: "No Expertise Selected",
        text: "Please select at least one expertise field before submitting your request.",
      });
      return; // Prevent form submission if no fields are selected
    }

    const form = event.target;
    const previousRole = role || "author";
    const newRole = "reviewer";
    const email = loggedInEmail;
    const message = form.description.value;

    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("previousRole", previousRole);
      formData.append("newRole", newRole);
      formData.append("message", message);
      formData.append("domain1", expertiseFields.domain1);
      formData.append("domain2", expertiseFields.domain2);
      formData.append("domain3", expertiseFields.domain3);
      formData.append("cv", cvFile);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/reviewer/newrequest`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.status === 201) {
        Swal.fire({
          icon: "success",
          title: "Reviewer Request sent Successfully",
          showConfirmButton: false,
          timer: 1500,
        });

        // Re-fetch the reviewerRequestStatus from the backend to reflect the latest status
        await checkApprovalStatus(); // This will fetch the updated reviewerRequestStatus
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reviewer-request-container">
      <div className="request-header">
        <h1 className="request-title">Reviewer Application</h1>
        <p className="request-subtitle">
          Submit your credentials to become a reviewer
        </p>
      </div>

      {reviewerRequestStatus === null && (
        <form onSubmit={handleSubmit} className="request-form">
          <div className="reviewerRequest-input-group">
            <label>Your Name</label>
            <input
              required
              type="text"
              placeholder="Your Name"
              disabled
              value={displayName}
            />
          </div>

          <div className="reviewerRequest-input-group">
            <label>Email Address</label>
            <input
              required
              type="email"
              value={loggedInEmail}
              disabled
              placeholder="Your Email"
            />
          </div>

          <div className="reviewerRequest-input-group">
            <label>Application Message</label>
            <textarea
              required
              name="description"
              placeholder="Explain why you want to become a reviewer..."
              rows="4"
            />
          </div>

          <div className="reviewerRequest-expertise-section">
            <h3>Expertise Domains</h3>
            <div className="reviewerRequest-domain-selectors">
              <div className="reviewerRequest-input-group">
                <select
                  name="domain1"
                  value={expertiseFields.domain1}
                  onChange={handleExpertiseChange}
                  required
                >
                  <option value="">Primary Expertise</option>
                  {domains.map((domain, index) => (
                    <option key={index} value={domain}>
                      {domain}
                    </option>
                  ))}
                </select>
              </div>

              <div className="reviewerRequest-input-group">
                <select
                  name="domain2"
                  value={expertiseFields.domain2}
                  onChange={handleExpertiseChange}
                >
                  <option value="">Secondary Expertise (Optional)</option>
                  {domains.map((domain, index) => (
                    <option key={index} value={domain}>
                      {domain}
                    </option>
                  ))}
                </select>
              </div>

              <div className="reviewerRequest-input-group">
                <select
                  name="domain3"
                  value={expertiseFields.domain3}
                  onChange={handleExpertiseChange}
                >
                  <option value="">Tertiary Expertise (Optional)</option>
                  {domains.map((domain, index) => (
                    <option key={index} value={domain}>
                      {domain}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="file-upload-group">
            <label className="file-upload-label">
              <CloudUploadOutlinedIcon className="upload-icon" />
              <span> {cvFile ? `${cvFile.name}` : "Upload CV (PDF only)"}</span>
              <input
                type="file"
                name="cv"
                accept=".pdf"
                className="file-input"
                onChange={handleCvUpload}
              />
            </label>
          </div>

          <button
            type="submit"
            className="reviewerRequest-submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="submit-loading">
                <span className="spinner"></span>
                Submitting...
              </div>
            ) : (
              "Submit Application"
            )}
          </button>
        </form>
      )}

      {reviewerRequestStatus === "submitted" && (
        <div className="reviewerRequest-status-card pending">
          <FaRegClock className="reviewerRequest-status-icon" />
          <h3>Application Under Review</h3>
          <p>
            Your reviewer request is being processed. Please wait for approval.
          </p>
        </div>
      )}

      {reviewerRequestStatus === "accepted" && (
        <div className="reviewerRequest-status-card approved">
          <FaUserCheck className="status-icon" />
          <h3>Approved Reviewer</h3>
          <p>
            Your reviewer status is active. Thank you for your contribution!
          </p>
        </div>
      )}

      {reviewerRequestStatus === "rejected" && (
        <div className="reviewerRequest-status-card rejected">
          <h3>Application Not Approved</h3>
          <p>Your reviewer request didn't meet the current requirements.</p>
        </div>
      )}
    </div>
  );
};

export default RequestForReviewer;
